import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type IncidentSeverity = "low" | "medium" | "high" | "critical";
export type IncidentCategory = "onboarding" | "billing" | "support" | "compliance" | "ops";
export type IncidentStatus = "open" | "acknowledged" | "resolved" | "suppressed";

export interface TenantIncident {
  id: string;
  company_id: string;
  tenant_name: string | null;
  severity: IncidentSeverity;
  category: IncidentCategory;
  title: string;
  summary: string;
  evidence: any[];
  recommended_actions: string[];
  status: IncidentStatus;
  requires_human_approval: boolean;
  source: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  suppressed_until: string | null;
  last_seen_at: string;
  occurrence_count: number;
  created_at: string;
  updated_at: string;
}

export interface IncidentFilters {
  status?: IncidentStatus | "all";
  severity?: IncidentSeverity | "all";
  category?: IncidentCategory | "all";
  companyId?: string;
}

export function useTenantIncidents(filters?: IncidentFilters) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["tenant-incidents", filters],
    queryFn: async () => {
      let query = supabase
        .from("tenant_incidents")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(100);

      if (filters?.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }
      if (filters?.severity && filters.severity !== "all") {
        query = query.eq("severity", filters.severity);
      }
      if (filters?.category && filters.category !== "all") {
        query = query.eq("category", filters.category);
      }
      if (filters?.companyId) {
        query = query.eq("company_id", filters.companyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as TenantIncident[];
    },
    enabled: !!user,
    refetchInterval: 60_000, // Refresh every minute
  });
}

export function useOpenIncidentCount() {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["tenant-incidents-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("tenant_incidents")
        .select("id", { count: "exact", head: true })
        .in("status", ["open", "acknowledged"]);

      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!user,
    refetchInterval: 60_000,
  });

  return data ?? 0;
}

export function useUpdateIncidentStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      incidentId: string;
      status: "acknowledged" | "resolved";
    }) => {
      const updates: Record<string, any> = { status: payload.status };

      if (payload.status === "acknowledged") {
        updates.acknowledged_at = new Date().toISOString();
      } else if (payload.status === "resolved") {
        updates.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("tenant_incidents")
        .update(updates)
        .eq("id", payload.incidentId);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-incidents"] });
      qc.invalidateQueries({ queryKey: ["tenant-incidents-count"] });
    },
  });
}
